import { AreaType, UserLocation } from '../../interfaces/interfaces';
import { getElasticGeopoint } from '../../utilities/locationUtils';

export const addGeoFiltersToQuery = (userLocation: UserLocation, boolQuery: any) => {
	const getAddressQueryFragment = (address: string) => ({ match_phrase: { UnparsedAddress: address } });
	const getPostalCodeQueryFragment = (postalCode: string) => ({ term: { PostalCode: postalCode } });
	const getCityQueryFragment = (city: string) => ({ match_phrase: { City: city } });
	const getCountyQueryFragment = (county: string) => ({
		match_phrase: { CountyOrParish: county.replace(/(county)/gi, '').trim() },
	});
	const getStateQueryFragment = (state: string) => ({ match: { StateOrProvince: state } });

	const geoQuerySearchObject = getGeoFilterFragment(userLocation);

	switch (userLocation.areaType) {
		case AreaType.Address:
			/*
			if (userLocation.address)
				boolQuery.filter.push(getAddressQueryFragment(userLocation.address));
			if (userLocation.zip)
				boolQuery.filter.push(getPostalCodeQueryFragment(userLocation.zip));
			else if (userLocation.city)
				boolQuery.filter.push(getCityQueryFragment(userLocation.city));
			else if (userLocation.county)
				boolQuery.filter.push(getCountyQueryFragment(userLocation.county));
			if (userLocation.state)
				boolQuery.filter.push(getStateQueryFragment(userLocation.state));
				*/
			if (geoQuerySearchObject) {
				boolQuery.filter.push(geoQuerySearchObject);
			}
			break;
		case AreaType.Zip:
			if (userLocation.zip) boolQuery.filter.push(getPostalCodeQueryFragment(userLocation.zip));
			break;
		case AreaType.City:
			if (userLocation.city) boolQuery.filter.push(getCityQueryFragment(userLocation.city));
			if (userLocation.state) boolQuery.filter.push(getStateQueryFragment(userLocation.state));
			break;
		case AreaType.County:
			if (userLocation.county) boolQuery.filter.push(getCountyQueryFragment(userLocation.county));
			if (userLocation.state) boolQuery.filter.push(getStateQueryFragment(userLocation.state));
			break;
		case AreaType.State:
			if (userLocation.state) boolQuery.filter.push(getStateQueryFragment(userLocation.state));
			break;
		case AreaType.GeoIp:
			if (userLocation.zip) boolQuery.filter.push(getPostalCodeQueryFragment(userLocation.zip));
			else if (userLocation.city) boolQuery.filter.push(getCityQueryFragment(userLocation.city));
			else if (userLocation.state) boolQuery.filter.push(getStateQueryFragment(userLocation.state));
			else if (geoQuerySearchObject) {
				boolQuery.filter.push(geoQuerySearchObject);
			}
			break;
		case AreaType.Geopoint:
			if (geoQuerySearchObject) {
				boolQuery.filter.push(geoQuerySearchObject);
			}
			break;
		case AreaType.Center:
			if (geoQuerySearchObject) {
				boolQuery.filter.push(geoQuerySearchObject);
			}
			break;
		default:
			if (userLocation.address) boolQuery.should.push(getAddressQueryFragment(userLocation.address));
			if (userLocation.zip) boolQuery.should.push(getPostalCodeQueryFragment(userLocation.zip));
			if (userLocation.city) boolQuery.should.push(getCityQueryFragment(userLocation.city));
			if (userLocation.county) boolQuery.should.push(getCountyQueryFragment(userLocation.county));
			if (userLocation.state) boolQuery.should.push(getStateQueryFragment(userLocation.state));
			if (geoQuerySearchObject) {
				boolQuery.should.push(geoQuerySearchObject);
			}
			if (boolQuery.should.length > 2) {
				boolQuery.minimum_should_match = 2;
			}
	}
};

export const getGeoFilterFragment = (userLocation?: UserLocation): any => {
	// Bounding box takes precedence over AAC - user is panning or zooming the map
	// User has already used AAC to go to a starting place

	if (userLocation?.boundsElastic) {
		return {
			geo_bounding_box: {
				location: userLocation?.boundsElastic,
			},
		};
	} else if (userLocation?.centerMap) {
		return {
			geo_distance: {
				//distance: '5mi',
				distance: `${Math.pow(23 - (userLocation?.zoomLevel ?? 10), 2)}km`,
				location: getElasticGeopoint(userLocation.centerMap),
			},
		};
	}
};

export const getSearchAreaFilterFragment = (searchArea: any) => {
	return {
		geo_shape: {
			location: {
				shape: {
					type: 'Polygon',
					relation: 'within',
					coordinates: [searchArea],
				},
			},
		},
	};
};

export const getBedroomFilterFragment = (bedroomsMin: number) => {
	return {
		range: {
			BedroomsTotal: {
				gte: bedroomsMin,
			},
		},
	};
};

export const getBathroomFilterFragment = (bathroomsMin: number) => {
	return {
		range: {
			RawMlsBathroomsTotal: {
				gte: bathroomsMin,
			},
		},
	};
};

export const getHasGarageFilterFragment = () => {
	return {
		term: {
			GarageYN: 'true',
		},
	};
};

export const getMinFilterFragment = (key: string, min: number) => {
	return {
		range: {
			[key]: {
				gte: min,
			},
		},
	};
};

export const getMaxFilterFragment = (key: string, max: number, filterMissingField = true) => {
	return filterMissingField
		? {
			range: {
				[key]: {
					lte: max,
				},
			},
		}
		: {
			bool: {
				should: [
					{
						range: {
							[key]: {
								lte: max,
							},
						},
					},

					{
						bool: {
							must_not: {
								exists: {
									field: key,
								},
							},
						},
					},
				],
			},
		};
};

export const getPriceFilterFragment = (priceMin: number, priceMax: number, propertyType: string) => {
	const priceFilterType = propertyType === 'RecentlySold' ? 'ClosePrice' : 'ListPrice';

	if (priceMax === -1)
		return {
			range: {
				[priceFilterType]: {
					gte: priceMin,
				},
			},
		};
	else
		return {
			range: {
				[priceFilterType]: {
					gte: priceMin,
					lte: priceMax,
				},
			},
		};
};

export const getRangeFilterFragment = (key: string, min: number, max: number) => {
	if (max === -1)
		return {
			range: {
				[key]: {
					gte: min,
				},
			},
		};
	else
		return {
			range: {
				[key]: {
					gte: min,
					lte: max,
				},
			},
		};
};

export const getHomeTypeFilterFragment = (homeTypes: string[]) => {
	const userFilterToHomeTypeMapping: any = {
		House: 'Single Family Residence',
		Townhouse: 'Townhouse',
		Condo: 'Condominium',
		Apartments: 'Apartment',
		Manufactured: 'Manufactured Home',
		Land: 'Unimproved Land',
		Other: 'Other',
	};

	const desiredHomeTypes = homeTypes.map(type => userFilterToHomeTypeMapping[`${type}`]);

	const homeTypeElasticfilter: any = {
		bool: {
			should: [],
		},
	};

	desiredHomeTypes.forEach(type =>
		homeTypeElasticfilter.bool.should.push({
			term: {
				'PropertySubType.keyword': type,
			},
		}),
	);

	return homeTypeElasticfilter;
};
// This is an optimization to have less data/fields return from ES
export function projectionFieldsToReturn(purposeOfData: string): Array<string> {
	if (purposeOfData === 'markersAndCards') {
		return [
			'id',
			'normalized_address',
			'ListPrice',
			'location',
			'ListOfficeName',
			'BedroomsTotal',
			'BuyerOfficeMlsId',
			'RawMlsBathroomsTotal',
			'LivingArea',
			'ccCounty',
			'CloseDate',
			'ClosePrice',
			'DerivedOnMarketTimestamp',
			'OffMarketTimestamp',
			'ListAgentFullName',
			'ListOfficeName',
			'ListAOR',
			'ListOfficeMlsId',
			'Latitude',
			'Longitude',
			'ListingId',
			'LotSizeAcres',
			'OnMarketDate',
			'Photos',
			'City',
			'CountyOrParish',
			'PostalCode',
			'StandardStatus',
			'StreetDirPrefix',
			'StreetSuffix',
			'StreetDirSuffix',
			'StreetName',
			'StreetNumber',
			'StateOrProvince',
			'OriginatingSystemName',
			'UnitNumber',
			'UnparsedAddress',
			'ListOfficeMlsId',
			'isCapcenterListing',
			'AddressUrlString',
			'StreetAddressUrl',
			'CityAddressUrl',
			'OpenHouse',
		];
	} else {
		return [
			'id',
			'AccessibilityFeatures',
			'Appliances',
			'AssociationFee',
			'AssociationFeeFrequency',
			'AssociationFee2',
			'AssociationFee2Frequency',
			'AssociationYN',
			'AttachedGarageYN',
			'Basement',
			'BedroomsTotal',
			'RawMlsBathroomsTotal',
			'BathroomsFull',
			'BathroomsHalf',
			'BuyerOfficeMlsId',
			'LivingArea',
			'CarportSpaces',
			'CarportYN',
			'ccCounty',
			'CountyOrParish',
			'ConstructionMaterials',
			'Cooling',
			'City',
			'DaysOnMarket',
			'DerivedOnMarketTimestamp',
			'OffMarketTimestamp',
			'ElementarySchool',
			'ExteriorFeatures',
			'Fencing',
			'FireplaceFeatures',
			'FireplacesTotal',
			'FireplaceYN',
			'Flooring',
			'InteriorFeatures',
			'GarageYN',
			'Heating',
			'HighSchool',
			'ListAOR',
			'ListingAgreement',
			'ListingId',
			'ListingService',
			'ListPrice',
			'ListOfficeName',
			'location',
			'ListOfficeMlsId',
			'ListOfficeName',
			'LotSizeAcres',
			'LotSizeArea',
			'MiddleOrJuniorSchool',
			'MlsStatus',
			'OnMarketDate',
			'Ownership',
			'OwnershipType',
			'ParcelNumber',
			'ParkingFeatures',
			'PoolFeatures',
			'PoolPrivateYN',
			'Photos',
			'PublicRemarks',
			'Roof',
			'PostalCode',
			'Sewer',
			'StandardStatus',
			'StreetName',
			'StreetNumber',
			'StateOrProvince',
			'Stories',
			'StoriesTotal',
			'SubdivisionName',
			'TaxAnnualAmount',
			'TaxAssessedValue',
			'TaxYear',
			'WaterSource',
			'YearBuilt',
			'Zoning',
			'OriginatingSystemName',
			'UnitNumber',
			'isCapcenterListing',
			'AddressUrlString',
			'StreetAddressUrl',
			'CityAddressUrl',
			'OpenHouse',
		];
	}
}
